import { useMemo } from 'react';
import { cloneDeep } from 'es-toolkit';

import {
  type RentalManagerUserPropertyDetailsWebApiHandler_details_Options,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyRecommendationsWebApiHandler_recommendations,
  useRentalManagerUserPropertyToursWebApiHandler_tours,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getCookieAsBoolean } from '../../utils/cookieUtils';

import { ZRM_USE_UPLIFT_BRAIN_TEST_DATA } from '../../constants/FeatureFlagConstants';
import { RECS_TO_MF_SECTION_MAP, REVIEW_PAGE_SECTIONS } from '../../constants/ListingDetailConstants';
import { RECS } from '../../constants/RecommendationsConstants';
import TimeConstants from '../../constants/TimeConstants';

import type {
  GetUserPropertyDetailsFloorPlanUnitWebDto,
  GetUserPropertyDetailsResponseWebDto,
  RentalManagerExternalTourDto,
  RentalManagerListingRecommendationDto,
  RentalManagerVrModelDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { RentalManagerUserPropertyRecommendationsWebApiHandler_recommendations_Options } from '@zg-rentals/ts-rental-manager-api-client-react-query';

export const getListingDetailsQueryKey = (listingAlias: string): ReadonlyArray<unknown> => {
  return useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({
    listingAlias,
    includeTopDupesDetails: true,
    includeVrModels: false,
    includeHasVrModels: false,
    includeWebAnalytics: true,
    includeDeliveryMethod: true,
  });
};

export const useGetListingDetails = (
  listingAlias: string,
  queryOptions?: Partial<RentalManagerUserPropertyDetailsWebApiHandler_details_Options>,
) => {
  return useRentalManagerUserPropertyDetailsWebApiHandler_details(
    {
      listingAlias,
      includeTopDupesDetails: true,
      includeVrModels: false,
      includeHasVrModels: false,
      includeWebAnalytics: true,
      includeDeliveryMethod: true,
    },
    {
      staleTime: TimeConstants.SECONDS_10,
      ...queryOptions,
      enabled: Boolean(listingAlias) && (queryOptions?.enabled ?? true),
      meta: {
        tags: ['listingDetails'],
        context: {
          api: 'details',
          details: { listingAlias },
        },
      },
    },
  );
};

/**
 * Extract unit number from tour label
 *
 * Handles formats like:
 * - "3620 Phinney Ave N #305 Seattle, WA 98103" → "305"
 * - "#305" → "305"
 * - "305" → "305"
 *
 * @param label Tour label string
 * @returns Unit number or null if not found
 */
function extractUnitNumberFromLabel(label: string): string | null {
  if (!label) return null;

  // Try to match "#305" pattern in the label
  const hashMatch = label.match(/#(\d+\w*)/);
  if (hashMatch) {
    return hashMatch[1];
  }

  // If label is just a unit number like "305", return it
  if (/^\d+\w*$/.test(label.trim())) {
    return label.trim();
  }

  return null;
}

/**
 * Check if tour matches unit by referenceId or label
 *
 * Matching rules (in order of priority):
 * 1. Exact match: tour.referenceId === unit.unitId
 * 2. Prefix match: tour.referenceId is a prefix of unit.unitId (e.g., "RentCafe/165233" matches "RentCafe/165233/304")
 * 3. Label match: extract unit number from tour.label and match against unit.name
 *
 * @param tour Tour object with referenceId and label
 * @param unit Unit object with unitId and name
 * @returns True if tour matches unit
 */
function tourMatchesUnit(
  tour: { referenceId: string; label?: string },
  unit: GetUserPropertyDetailsFloorPlanUnitWebDto,
): boolean {
  // 1. Exact match
  if (tour.referenceId === unit.id) {
    return true;
  }

  // 2. Prefix match - tour.referenceId is a prefix of unit.id
  const tourParts = tour.referenceId.split('/');
  if (tourParts.length >= 3 && unit.id.startsWith(tour.referenceId + '/')) {
    // Also check label if available for extra validation
    if (tour.label) {
      const unitNumber = extractUnitNumberFromLabel(tour.label);
      if (unitNumber && unitNumber === unit.name) {
        return true; // Prefix + label both match - high confidence
      }
      // Prefix matches but label doesn't - still accept it
      return true;
    }
    return true;
  }

  // 3. Label match - extract unit number from label and match against unit.name
  if (tour.label) {
    const unitNumber = extractUnitNumberFromLabel(tour.label);
    if (unitNumber && unitNumber === unit.name) {
      return true;
    }
  }

  return false;
}

/**
 * Fetch tours and sync them to floorplans and units
 *
 * This hook:
 * - Fetches tour data from the API
 * - Matches tours to floorplans and units using intelligent matching:
 *   - Exact referenceId match
 *   - Prefix matching for unit-level tours only (3+ parts in referenceId)
 *   - Label extraction (tour.label = "3620 Phinney Ave N #305..." extracts "305" and matches unit.name)
 * - Returns synced tours, floorplans, and units with tours attached
 *
 * @param listingAlias The listing alias to fetch tours for
 * @param listingDetails Listing details with floorplans/units
 * @returns Synced tours, floorplans, and units with tours attached
 */
export const useGetTours = (listingAlias: string, listingDetails?: GetUserPropertyDetailsResponseWebDto) => {
  // Fetch tours data from API
  const { data: toursData, isLoading } = useRentalManagerUserPropertyToursWebApiHandler_tours(
    { propertyId: listingAlias },
    { enabled: Boolean(listingAlias && listingDetails), meta: { tags: ['tours'] } },
  );

  // Sync tours to floorplans/units
  const syncedData = useMemo(() => {
    // Return empty structure if data isn't ready
    if (!toursData || !listingDetails) {
      return {
        vrModels: [],
        externalTours: [],
        floorplans: [],
        units: [],
      };
    }

    const units = listingDetails?.units || [];

    const copyFloorplans = cloneDeep(listingDetails?.floorPlans || []);
    const copyUnits = cloneDeep(units);

    const vrModels = cloneDeep(toursData.vrModels || []);
    const externalTours = cloneDeep(toursData.externalTours || []);

    const vrModelsMap: Record<string, RentalManagerVrModelDto> = {};
    const externalToursMap: Record<string, RentalManagerExternalTourDto> = {};

    // Build maps by referenceId for fast lookup
    vrModels.forEach((tour) => {
      vrModelsMap[tour.referenceId] = tour;
    });
    externalTours.forEach((tour) => {
      externalToursMap[tour.referenceId] = tour;
    });

    // Match tours to floorplans
    copyFloorplans.forEach((floorplan, floorplanIndex: number) => {
      // Exact match on floorplan ID
      if (vrModelsMap[floorplan.id]) {
        copyFloorplans[floorplanIndex].vrModel = {
          ...vrModelsMap[floorplan.id],
          creatorZuid: String(vrModelsMap[floorplan.id].creatorZuid),
        };
        const indexOfVrModel = vrModels.findIndex((element) => element.referenceId === floorplan.id);
        if (indexOfVrModel > -1) {
          vrModels.splice(indexOfVrModel, 1);
        }
      }

      if (externalToursMap[floorplan.id]) {
        copyFloorplans[floorplanIndex].externalTour = externalToursMap[floorplan.id];
        const indexOfExternalTour = externalTours.findIndex((element) => element.referenceId === floorplan.id);
        if (indexOfExternalTour > -1) {
          externalTours.splice(indexOfExternalTour, 1);
        }
      }

      // Match tours to units within floorplan (FIXED LOGIC)
      if (floorplan.units && floorplan.units.length > 0) {
        floorplan.units.forEach((unit, unitIndex: number) => {
          // Try to match vrModels to unit (exact, prefix, or label match)
          for (let i = vrModels.length - 1; i >= 0; i--) {
            const tour = vrModels[i];
            if (tourMatchesUnit(tour, unit)) {
              copyFloorplans[floorplanIndex].units[unitIndex].vrModel = {
                ...tour,
                creatorZuid: String(tour.creatorZuid),
              };
              vrModels.splice(i, 1);
              break; // Only attach one tour per unit
            }
          }

          // Try to match externalTours to unit (exact, prefix, or label match)
          for (let i = externalTours.length - 1; i >= 0; i--) {
            const tour = externalTours[i];
            if (tourMatchesUnit(tour, unit)) {
              copyFloorplans[floorplanIndex].units[unitIndex].externalTour = tour;
              externalTours.splice(i, 1);
              break; // Only attach one tour per unit
            }
          }
        });
      }
    });

    // Match tours to standalone units (no floorplan) (FIXED LOGIC)
    copyUnits.forEach((unit: GetUserPropertyDetailsFloorPlanUnitWebDto, unitIndex: number) => {
      // Try to match vrModels to unit
      for (let i = vrModels.length - 1; i >= 0; i--) {
        const tour = vrModels[i];
        if (tourMatchesUnit(tour, unit)) {
          copyUnits[unitIndex].vrModel = {
            ...tour,
            creatorZuid: String(tour.creatorZuid),
          };
          vrModels.splice(i, 1);
          break;
        }
      }

      // Try to match externalTours to unit
      for (let i = externalTours.length - 1; i >= 0; i--) {
        const tour = externalTours[i];
        if (tourMatchesUnit(tour, unit)) {
          copyUnits[unitIndex].externalTour = tour;
          externalTours.splice(i, 1);
          break;
        }
      }
    });

    // Filter remaining tours to only keep property-level tours
    // (referenceId with 2 parts like "RentCafe/165233", not 3 like "RentCafe/165233/304")
    const propertyLevelVrModels = vrModels.filter((vrModel) => {
      const referenceId = vrModel.referenceId.split('/');
      return referenceId.length === 2;
    });

    const propertyLevelExternalTours = externalTours.filter((externalTour) => {
      const referenceId = externalTour.referenceId.split('/');
      return referenceId.length === 2;
    });

    return {
      vrModels: propertyLevelVrModels,
      externalTours: propertyLevelExternalTours,
      floorplans: copyFloorplans,
      units: copyUnits,
    };
  }, [toursData, listingDetails]);

  return {
    ...syncedData,
    isLoading,
  };
};

export type RecommendationsBySection = Record<string, Record<string, RentalManagerListingRecommendationDto>>;

export const useGetListingRecommendations = (
  {
    listingAlias = '',
    isMultifamily,
  }: {
    listingAlias?: string;
    isMultifamily?: boolean;
  },
  options: Omit<
    RentalManagerUserPropertyRecommendationsWebApiHandler_recommendations_Options,
    'queryFn' | 'queryKey'
  > = {},
) => {
  const useUpliftBrainTestData = getCookieAsBoolean(ZRM_USE_UPLIFT_BRAIN_TEST_DATA);

  const {
    data: recommendationsData,
    error,
    isLoading,
    isFetching,
  } = useRentalManagerUserPropertyRecommendationsWebApiHandler_recommendations(
    { listingAlias, useUpliftBrainTestData },
    { meta: { tags: ['listingRecommendations'] }, enabled: !!listingAlias && (options.enabled ?? true), ...options },
  );

  const recommendationsBySection = useMemo(() => {
    if (!listingAlias || isMultifamily === undefined || !recommendationsData?.recommendations) {
      return {};
    }

    const sections: RecommendationsBySection = isMultifamily
      ? Object.values(RECS_TO_MF_SECTION_MAP).reduce((acc, section) => {
          acc[section] = {};
          return acc;
        }, {} as RecommendationsBySection)
      : {
          [REVIEW_PAGE_SECTIONS.MEDIA]: {},
          [REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION]: {},
          [REVIEW_PAGE_SECTIONS.AMENITIES]: {},
          [REVIEW_PAGE_SECTIONS.RENT_DETAILS]: {},
          [REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA]: {},
          [REVIEW_PAGE_SECTIONS.CONTACT]: {},
          [REVIEW_PAGE_SECTIONS.COSTS_AND_FEES]: {},
        };

    recommendationsData.recommendations
      .filter((rec) => !rec.isComplete)
      .forEach((rec) => {
        if (isMultifamily) {
          const section = RECS_TO_MF_SECTION_MAP[rec.type];
          if (section) {
            sections[section][rec.type] = rec;
          }
        } else {
          if (rec.type === RECS.PHOTOS || rec.type === RECS.VIRTUAL_TOUR) {
            sections[REVIEW_PAGE_SECTIONS.MEDIA][rec.type] = rec;
          } else if (
            rec.type === RECS.HIDE_STREET_ADDRESS ||
            rec.type === RECS.SQUARE_FOOTAGE ||
            rec.type === RECS.DESCRIPTION
          ) {
            sections[REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION][rec.type] = rec;
          } else if (rec.type === RECS.AMENITIES || rec.type === RECS.LAUNDRY || rec.type === RECS.PARKING) {
            sections[REVIEW_PAGE_SECTIONS.AMENITIES][rec.type] = rec;
          } else if (rec.type === RECS.SECURITY_DEPOSIT || rec.type === RECS.PRICE) {
            sections[REVIEW_PAGE_SECTIONS.RENT_DETAILS][rec.type] = rec;
          } else if (rec.type === RECS.PETS_NOT_ALLOWED) {
            sections[REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA][rec.type] = rec;
          } else if (
            rec.type === RECS.LEASE_LENGTH ||
            rec.type === RECS.LEASE_TERMS ||
            rec.type === RECS.SCHEDULE ||
            rec.type === RECS.APPLICATIONS ||
            rec.type === RECS.HIDE_PHONE ||
            rec.type === RECS.CONTACT ||
            rec.type === RECS.DATE_AVAILABLE
          ) {
            sections[REVIEW_PAGE_SECTIONS.CONTACT][rec.type] = rec;
          }
        }
      });

    return sections;
  }, [listingAlias, isMultifamily, recommendationsData]);

  return { recommendationsData, recommendationsBySection, error, isLoading, isFetching };
};
